<script setup lang="ts">
import {useData} from "vike-vue/useData";
import {computed, ref, watch} from "vue";
import {usePageContext} from "vike-vue/usePageContext";
import ProductDesign from "../../../core/cpn/web/banner/ProductDesign.vue";

const data: any = useData();
const pageContext = usePageContext();

const currentPage = ref(1);
const categoryPath = ref("");

const category = computed(() => data.category);

const parseFromPath = (path: string) => {
  const segments = path.split("/").filter(Boolean);
  if (segments.length > 0) {
    const last = segments[segments.length - 1];
    if (!Number.isNaN(Number(last))) {
      currentPage.value = Number(last);
      categoryPath.value = "/" + segments.slice(0, -1).join("/");
      return;
    }
  }
  currentPage.value = 1;
  categoryPath.value = "/" + segments.join("/");
};

const urlPathname = computed(() => pageContext.urlPathname ?? (typeof window !== "undefined" ? window.location.pathname : ""));

watch(
  urlPathname,
  (p) => {
    parseFromPath(p || "");
  },
  {immediate: true}
);
</script>

<template>
  <div v-if="data && data.category" c2c-0 class="category-page">
    <section c2c-1 class="hero">
      <p c2c-2 class="hero-kicker">Collection</p>
      <h1>{{ category.name }}</h1>
      <p c2c-3 class="hero-desc">
        Browse available designs in this category. Use pagination to explore all templates.
      </p>
    </section>

    <section c2c-4 class="design-section">
      <ul c2c-5 class="design-grid">
        <li v-for="item in data.banners" :key="item._id">
          <ProductDesign :design="item.detail"></ProductDesign>
        </li>
      </ul>
    </section>

    <section c2c-6 class="paging-section" v-if="data.totalPage > 1">
      <p c2c-7 class="page-label">Pages</p>

      <div c2c-8 class="pager-actions">
        <a
          v-if="currentPage > 1"
          c2c-9 class="pager-btn"
          :href="currentPage - 1 === 1 ? categoryPath : `${categoryPath}/${currentPage - 1}`"
        >
          Prev
        </a>
        <a
          v-if="currentPage < data.totalPage"
          c2c-10 class="pager-btn"
          :href="`${categoryPath}/${currentPage + 1}`"
        >
          Next
        </a>
      </div>

      <ul c2c-11 class="pager-list">
        <li v-for="p in data.totalPage" :key="p">
          <a
            c2c-12 class="pager-page"
            :class="{active: p === currentPage}"
            :href="p === 1 ? categoryPath : `${categoryPath}/${p}`"
          >
            {{ p }}
          </a>
        </li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.category-page {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 14px 44px;
  color: #0f172a;
}

.hero {
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 24px 20px;
  background:
    radial-gradient(120% 180% at 100% 0%, rgba(56, 189, 248, 0.12) 0%, rgba(56, 189, 248, 0) 55%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.hero-kicker {
  margin: 0;
  color: #0369a1;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1 {
  margin: 8px 0 0;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.08;
}

.hero-desc {
  max-width: 760px;
  margin: 12px 0 0;
  color: #334155;
  font-size: 16px;
  line-height: 1.65;
}

.design-section {
  margin-top: 24px;
}

.design-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.paging-section {
  margin-top: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  padding: 16px;
}

.page-label {
  margin: 0;
  text-align: center;
  font-style: italic;
  color: #64748b;
  font-size: 13px;
}

.pager-actions {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 9px;
  color: #0f172a;
  font-weight: 700;
  text-decoration: none;
  background: #f8fafc;
}

.pager-btn:hover {
  border-color: #0ea5e9;
  color: #0c4a6e;
}

.pager-list {
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.pager-page {
  width: 42px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  text-decoration: none;
  font-weight: 700;
  background: #ffffff;
}

.pager-page:hover {
  border-color: #0ea5e9;
  color: #0c4a6e;
}

.pager-page.active {
  border-color: #0ea5e9;
  background: #ecfeff;
  color: #0c4a6e;
}

@media (max-width: 1100px) {
  .design-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .design-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .category-page {
    padding: 16px 10px 36px;
  }

  .hero {
    padding: 18px 14px;
  }

  .design-grid {
    grid-template-columns: 1fr;
  }
}
</style>
<style lang="scss" scoped></style>